import React from 'react';
import { Link } from 'react-router';
import { ArrowRightIcon, EnvelopeIcon, CheckIcon, MinusIcon } from '@heroicons/react/24/outline';

// Shared "deck-aesthetic" section building blocks. All pieces assume the
// page is rendered on a pure-black PublicLayout. Sections compose on top of
// each other with a thin white/5 divider line. Visual language matches the
// CallCov sales deck: light typography, sky-400 accents, amber-400 for
// "before / today" stats, no shadows, no rounded cards, no gradients.

type Tone = 'sky' | 'amber' | 'red';

const toneTextClass: Record<Tone, string> = {
  sky: 'text-sky-400',
  amber: 'text-amber-400',
  red: 'text-red-400',
};

// ----- Section wrapper with optional top divider -----

interface DeckSectionProps {
  children: React.ReactNode;
  id?: string;
  divider?: boolean;
  size?: 'compact' | 'standard' | 'large';
}

const sizeClasses: Record<NonNullable<DeckSectionProps['size']>, string> = {
  compact: 'py-16 lg:py-20',
  standard: 'py-24 lg:py-32',
  large: 'py-32 lg:py-40',
};

export const DeckSection: React.FC<DeckSectionProps> = ({
  children,
  id,
  divider = true,
  size = 'standard',
}) => (
  // scroll-mt-24 (~96px) leaves headroom for the fixed nav so #anchor
  // navigation lands the section title under the nav, not behind it.
  <section
    id={id}
    className={`bg-black ${divider ? 'border-t border-white/5' : ''} ${
      id ? 'scroll-mt-24' : ''
    }`}
  >
    <div className={`container-custom ${sizeClasses[size]}`}>{children}</div>
  </section>
);

// ----- Section header (badge + huge light title + optional subtitle) -----

interface DeckSectionHeaderProps {
  badge?: string;
  title: React.ReactNode;
  subtitle?: string;
  align?: 'left' | 'center';
  maxWidth?: string;
}

export const DeckSectionHeader: React.FC<DeckSectionHeaderProps> = ({
  badge,
  title,
  subtitle,
  align = 'left',
  maxWidth = 'max-w-3xl',
}) => (
  <div
    className={`${maxWidth} mb-14 animate-on-scroll ${
      align === 'center' ? 'mx-auto text-center' : ''
    }`}
  >
    {badge && (
      <p className="text-sm uppercase tracking-[0.2em] text-white/60 mb-4">{badge}</p>
    )}
    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight">
      {title}
    </h2>
    {subtitle && <p className="mt-6 text-lg text-white/50">{subtitle}</p>}
  </div>
);

// ----- Hero block (slide 1 style) -----

interface DeckHeroCta {
  label: string;
  to: string;
  external?: boolean;
}

interface DeckHeroProps {
  title: React.ReactNode;
  subtitle?: string;
  primaryCta?: DeckHeroCta;
  secondaryCta?: DeckHeroCta;
  announcement?: string;
  glow?: boolean;
}

export const DeckHero: React.FC<DeckHeroProps> = ({
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  announcement,
  glow = true,
}) => (
  <section className="relative bg-black overflow-hidden">
    {glow && (
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 80% 20%, rgba(14,165,233,0.10), transparent 60%)',
        }}
      />
    )}
    <div className="container-custom pt-40 pb-32 lg:pt-52 lg:pb-40 relative">
      <div className="max-w-5xl">
        <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tight text-white leading-[1.05]">
          {title}
        </h1>
        {subtitle && (
          <>
            <div className="mt-8 h-px w-24 bg-white/30" />
            <p className="mt-8 text-lg sm:text-xl text-white/60 max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          </>
        )}
        {(primaryCta || secondaryCta) && (
          <div className="mt-12 flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
            {primaryCta && (
              <Link
                to={primaryCta.to}
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white text-base font-medium px-7 py-3.5 transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              >
                {primaryCta.label}
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            )}
            {secondaryCta && (
              <Link
                to={secondaryCta.to}
                className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black text-base font-medium px-2 py-3.5 transition-colors group"
              >
                {secondaryCta.label}
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            )}
          </div>
        )}
        {announcement && <p className="mt-10 text-sm text-white/60">{announcement}</p>}
      </div>
    </div>
  </section>
);

// ----- Two-stat side-by-side with vertical divider (slide 2 style) -----

interface DeckStatPairProps {
  before: { value: string; label: string; caption: string };
  after: { value: string; label: string; caption: string };
  beforeTone?: Tone;
  afterTone?: Tone;
}

export const DeckStatPair: React.FC<DeckStatPairProps> = ({
  before,
  after,
  beforeTone = 'amber',
  afterTone = 'sky',
}) => (
  <div className="grid md:grid-cols-2 gap-12 md:gap-0 animate-on-scroll">
    <div className="md:pr-12">
      <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-6">{before.caption}</p>
      <div className={`text-7xl sm:text-8xl font-light ${toneTextClass[beforeTone]} mb-6 leading-none`}>
        {before.value}
      </div>
      <p className="text-base text-white/60">{before.label}</p>
    </div>
    <div className="md:pl-12 md:border-l md:border-white/10">
      <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-6">{after.caption}</p>
      <div className={`text-7xl sm:text-8xl font-light ${toneTextClass[afterTone]} mb-6 leading-none`}>
        {after.value}
      </div>
      <p className="text-base text-white/60">{after.label}</p>
    </div>
  </div>
);

// ----- Horizontal step grid with numerals + arrows (slides 6 & 9) -----

export interface DeckStep {
  number: string;
  title: string;
  description: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

interface DeckStepGridProps {
  steps: DeckStep[];
}

export const DeckStepGrid: React.FC<DeckStepGridProps> = ({ steps }) => (
  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8 relative">
    {steps.map((step, idx) => (
      <div key={step.number} className="relative animate-on-scroll">
        <div className="text-5xl font-light text-sky-400 mb-4 leading-none">{step.number}</div>
        {step.icon && <step.icon className="w-5 h-5 text-white/60 mb-4" />}
        <h3 className="text-lg font-medium text-white mb-3">{step.title}</h3>
        <p className="text-sm text-white/50 leading-relaxed">{step.description}</p>
        {idx < steps.length - 1 && (
          <ArrowRightIcon
            className="hidden lg:block absolute top-3 -right-6 w-5 h-5 text-white/20"
            aria-hidden="true"
          />
        )}
      </div>
    ))}
  </div>
);

// ----- Pillar grid (slide 7 style: small icon + title + description) -----

export interface DeckPillar {
  title: string;
  description: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tone?: Tone;
}

interface DeckPillarGridProps {
  pillars: DeckPillar[];
  columns?: 2 | 3 | 4;
}

const columnsClass = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-2 lg:grid-cols-4',
} as const;

export const DeckPillarGrid: React.FC<DeckPillarGridProps> = ({ pillars, columns = 3 }) => (
  <div className={`grid ${columnsClass[columns]} gap-x-12 gap-y-10`}>
    {pillars.map((pillar) => (
      <div key={pillar.title} className="animate-on-scroll">
        {pillar.icon && (
          <pillar.icon className={`w-6 h-6 ${toneTextClass[pillar.tone ?? 'sky']} mb-4`} />
        )}
        <h3 className="text-lg font-medium text-white mb-3">{pillar.title}</h3>
        <p className="text-sm text-white/50 leading-relaxed">{pillar.description}</p>
      </div>
    ))}
  </div>
);

// ----- Final CTA (slide 10 style: centered logo, light heading, mailto, link) -----

interface DeckFinalCtaProps {
  title: string;
  subtitle?: string;
  email?: string;
  cta?: DeckHeroCta;
  showLogo?: boolean;
}

export const DeckFinalCta: React.FC<DeckFinalCtaProps> = ({
  title,
  subtitle,
  email,
  cta,
  showLogo = true,
}) => (
  <DeckSection size="large">
    <div className="max-w-3xl mx-auto text-center">
      {showLogo && (
        <img
          src="/logos/callcov-white.svg"
          alt="CallCov"
          className="h-16 w-auto mx-auto mb-12 animate-on-scroll"
        />
      )}
      <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight animate-on-scroll">
        {title}
      </h2>
      {subtitle && <p className="mt-8 text-lg text-white/60 animate-on-scroll">{subtitle}</p>}
      {(email || cta) && (
        <div className="mt-16 mx-auto max-w-md animate-on-scroll">
          <div className="border-t border-white/10 pt-10 space-y-6">
            {email && (
              <a
                href={`mailto:${email}`}
                className="inline-flex items-center gap-3 text-base text-white/80 hover:text-white transition-colors"
              >
                <EnvelopeIcon className="w-5 h-5" />
                {email}
              </a>
            )}
            {cta && (
              <div>
                <Link
                  to={cta.to}
                  className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black text-base font-medium transition-colors group"
                >
                  {cta.label}
                  <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </DeckSection>
);

// ----- Disclaimer banner (used right under hero) -----

interface DeckDisclaimerProps {
  text: string;
  linkLabel?: string;
  linkTo?: string;
}

export const DeckDisclaimer: React.FC<DeckDisclaimerProps> = ({ text, linkLabel, linkTo }) => (
  <div className="bg-black border-y border-white/5">
    <div className="container-custom py-3">
      <p className="text-xs text-white/50 text-center">
        {text}
        {linkLabel && linkTo && (
          <>
            {' '}
            <Link to={linkTo} className="underline hover:text-white/80">
              {linkLabel}
            </Link>
          </>
        )}
      </p>
    </div>
  </div>
);

// ----- Inline FAQ (deck-style: question + answer, thin dividers) -----

export interface DeckFaqItem {
  question: string;
  answer: string;
}

interface DeckFaqListProps {
  items: DeckFaqItem[];
}

export const DeckFaqList: React.FC<DeckFaqListProps> = ({ items }) => (
  <div className="divide-y divide-white/10 max-w-4xl">
    {items.map((item, idx) => (
      <div key={idx} className="py-8 animate-on-scroll">
        <h3 className="text-xl font-medium text-white mb-4">{item.question}</h3>
        <p className="text-base text-white/60 leading-relaxed">{item.answer}</p>
      </div>
    ))}
  </div>
);

// ----- Pricing card (deck-style: thin border, no shadow, light typography) -----

export interface DeckPricingTier {
  name: string;
  price: React.ReactNode;
  priceCaption?: string;
  description: string;
  features: string[];
  notIncluded?: string[];
  cta: { label: string; to: string };
  highlight?: boolean;
  badge?: string;
}

interface DeckPricingGridProps {
  tiers: DeckPricingTier[];
  includeLabel?: string;
  notIncludeLabel?: string;
}

export const DeckPricingGrid: React.FC<DeckPricingGridProps> = ({
  tiers,
  includeLabel = 'Incluye',
  notIncludeLabel = 'No incluye',
}) => (
  <div
    className={`grid gap-6 ${
      tiers.length >= 4 ? 'lg:grid-cols-4' : tiers.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
    }`}
  >
    {tiers.map((tier) => (
      <div
        key={tier.name}
        className={`relative flex flex-col p-8 border ${
          tier.highlight ? 'border-sky-500 bg-sky-500/[0.03]' : 'border-white/10'
        } animate-on-scroll`}
      >
        {tier.badge && (
          <p className="text-xs uppercase tracking-[0.2em] text-sky-400 mb-4">{tier.badge}</p>
        )}
        <h3 className="text-xl font-medium text-white mb-2">{tier.name}</h3>
        <p className="text-sm text-white/50 mb-6 leading-relaxed">{tier.description}</p>
        <div className="mb-2">
          <div className="text-4xl font-light text-white leading-none">{tier.price}</div>
          {tier.priceCaption && (
            <p className="text-xs text-white/60 mt-2">{tier.priceCaption}</p>
          )}
        </div>
        <Link
          to={tier.cta.to}
          className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
            tier.highlight
              ? 'bg-sky-600 hover:bg-sky-500 text-white'
              : 'border border-white/20 hover:border-white/40 text-white'
          }`}
        >
          {tier.cta.label}
        </Link>
        <div className="mt-8 flex-1">
          <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-4">{includeLabel}</p>
          <ul className="space-y-3 mb-6">
            {tier.features.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                <CheckIcon className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          {tier.notIncluded && tier.notIncluded.length > 0 && (
            <>
              <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-4">
                {notIncludeLabel}
              </p>
              <ul className="space-y-3">
                {tier.notIncluded.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/60">
                    <MinusIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    ))}
  </div>
);

// ----- Two-column with vertical divider (use cases, contact options, etc.) -----

interface DeckTwoColumnProps {
  left: React.ReactNode;
  right: React.ReactNode;
}

export const DeckTwoColumn: React.FC<DeckTwoColumnProps> = ({ left, right }) => (
  <div className="grid md:grid-cols-2 gap-x-12 gap-y-16">
    <div className="animate-on-scroll">{left}</div>
    <div className="animate-on-scroll md:border-l md:border-white/10 md:pl-12">{right}</div>
  </div>
);

// ----- Scroll-in observer hook (shared across deck pages) -----

export const useDeckAnimateOnScroll = (): void => {
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};
